import { ShowHide } from '@/components';
import { useMoveSheetLine } from '@/modules/move/hooks';
import { EditedRow } from '@/modules/move/hooks/useMoveEdition/useMoveEdition';
import { groupBy } from 'lodash';
import ICONS from '@/shared/entities/constants/icons';
import React, { useEffect, useMemo, useState } from 'react';
import { Accordion, Text } from 'vkit/lib/components';
import { Grid } from 'vkit/lib/context';
import { COLUMN_REPORT_LIST_ITEM, ReportListItemContent, ReportListItemHeader } from '..';
import { MOVE_ERROR_TYPE, MoveSheetNormalized, MoveSheetNormalizedContent } from '../../entities';

interface ReportListItemProps {
  isOnlyError: boolean;
  moveSheetNormalized: MoveSheetNormalized;
  editedRow?: EditedRow;
  setRowToEdition?: (move: MoveSheetNormalized) => void;
}

export const ReportListItem: React.FC<ReportListItemProps> = ({
  isOnlyError,
  moveSheetNormalized,
  editedRow,
  setRowToEdition,
}) => {
  const [isExpanded, setExpanded] = useState<boolean>(isOnlyError);
  const { loadMoveSheetLine, isLoading, sheetLine } = useMoveSheetLine({
    rowId: moveSheetNormalized.id,
  });

  const handleExpanded = (isExpanded: boolean) => {
    setExpanded(isExpanded);
  };

  const showContent = useMemo((): Record<string, MoveSheetNormalizedContent[]> | undefined => {
    const content = moveSheetNormalized.content || sheetLine?.content;

    return groupBy(content, (item) =>
      item.belongsToLayout ? 'belongsToLayout' : 'notBelongsToLayout',
    );
  }, [moveSheetNormalized.content, sheetLine]);

  useEffect(() => {
    if (!isExpanded || moveSheetNormalized.content || !moveSheetNormalized.id) {
      return;
    }
    loadMoveSheetLine(moveSheetNormalized.id);
  }, [isExpanded, moveSheetNormalized.content, moveSheetNormalized.id, loadMoveSheetLine]);

  return (
    <Accordion
      full
      box
      expanded={isExpanded}
      handleExpanded={handleExpanded}
      overflowVisibleWhenOpen
      title={
        <ReportListItemHeader
          moveSheetNormalized={moveSheetNormalized}
          editedRow={editedRow}
          setRowToEdition={setRowToEdition}
        />
      }>
      <Grid column gap={16}>
        <ShowHide
          visible={moveSheetNormalized.hasError && moveSheetNormalized.messageWarning}
          transition='slideToDown'>
          <Text size='medium' icon={ICONS.WARNING} value={moveSheetNormalized.messageWarning!} />
        </ShowHide>

        {moveSheetNormalized.hasError &&
          moveSheetNormalized.errorType === MOVE_ERROR_TYPE.REVIEW_ERROR && (
            <>
              <Grid column gap={4}>
                <Text value='Crítica(s)' title />
                {moveSheetNormalized.review?.errors?.map((error: string) => (
                  <Text value={error} size='medium' key={error} />
                ))}
              </Grid>
              <Text value='Dados da movimentação' title />
            </>
          )}

        {Number(showContent?.notBelongsToLayout?.length) > 0 && (
          <ReportListItemContent
            isLoading={isLoading}
            sheetLineId={moveSheetNormalized.id}
            content={showContent?.notBelongsToLayout}
            moveSheetNormalized={moveSheetNormalized}
            editedRow={editedRow}
            showOnlyErrors={isOnlyError}
            columnKeys={[
              COLUMN_REPORT_LIST_ITEM.NAME,
              COLUMN_REPORT_LIST_ITEM.CPF,
              COLUMN_REPORT_LIST_ITEM.ERROR,
            ]}
          />
        )}

        {Number(showContent?.belongsToLayout?.length) > 0 && (
          <ReportListItemContent
            isLoading={isLoading}
            sheetLineId={moveSheetNormalized.id}
            content={showContent?.belongsToLayout}
            moveSheetNormalized={moveSheetNormalized}
            editedRow={editedRow}
            showOnlyErrors={isOnlyError}
            columnKeys={[
              COLUMN_REPORT_LIST_ITEM.COLUMN,
              COLUMN_REPORT_LIST_ITEM.VALUE,
              COLUMN_REPORT_LIST_ITEM.EDITION_VALUE,
              COLUMN_REPORT_LIST_ITEM.REASON,
            ]}
          />
        )}
      </Grid>
    </Accordion>
  );
};
